import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, Save, Link, Edit, X, Trash2, Upload, FileText } from 'lucide-react';
import { getCourseById, updateLesson, deleteLesson, addLessonToModule } from '../services/firebase/courses';
import { useTheme } from '../contexts/ThemeContext';
import BackButton from '../components/BackButton';
import EditButton from '../components/EditButton';
import SaveButton from '../components/SaveButton';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import { getAuth } from 'firebase/auth';
import PortalBackground from '../components/PortalBackground';
import BBALoader from '../components/BBALoader';
import CourseVideoUploader from '../components/crm/CourseVideoUploader';

const LessonOverview = () => {
  const { courseId, lessonId } = useParams();
  const navigate = useNavigate();
  const { darkMode: isDarkMode } = useTheme();
  const isNewLesson = !lessonId || lessonId === 'new';

  // Lesson state
  const [title, setTitle] = useState('');
  const [selectedModule, setSelectedModule] = useState('');
  const [description, setDescription] = useState('');
  const [videoUrl, setVideoUrl] = useState(''); // Store the download URL
  const [videoLength, setVideoLength] = useState('');
  const [videoUploading, setVideoUploading] = useState(false);
  const [editMode, setEditMode] = useState(isNewLesson);
  const [backup, setBackup] = useState({});
  const [documents, setDocuments] = useState([]);
  const [order, setOrder] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modules, setModules] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Fetch course and lesson data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const courseData = await getCourseById(courseId);
        console.log('Fetched course data:', courseData); // Debug log

        if (courseData) {
          const courseModules = courseData.modules || [];
          console.log('Course modules:', courseModules); // Debug log
          setModules(courseModules);
          
          if (!isNewLesson) {
            // Find the lesson in the modules
            let foundLesson = null;
            let foundModuleId = null;

            for (const module of courseModules) {
              const lesson = module.lessons?.find(l => l.id === lessonId);
              if (lesson) {
                foundLesson = lesson;
                foundModuleId = module.id;
                break;
              }
            }

            if (foundLesson) {
              setTitle(foundLesson.title);
              setSelectedModule(foundModuleId);
              setDescription(foundLesson.description);
              setVideoUrl(foundLesson.videoUrl || '');
              setVideoLength(foundLesson.videoLength || '');
              setDocuments(foundLesson.documents || []);
              setOrder(foundLesson.order);
            } else {
              setError('Lesson not found');
            }
          } else {
            // For new lessons, set default values
            setOrder(1);
            if (courseModules.length > 0) {
              const firstModuleId = courseModules[0].id;
              console.log('Setting first module ID:', firstModuleId); // Debug log
              setSelectedModule(firstModuleId);
            }
          }
        }
        setError(null);
      } catch (err) {
        console.error('Error fetching lesson:', err);
        setError('Failed to load lesson data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [courseId, lessonId, isNewLesson]);

  const uploadDocumentToStorage = async (file) => {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User not authenticated. Please log in again.');
    }
    const safeName = String(file.name || 'file').replace(/[^\w.\-]+/g, '_');
    const path = `lesson-documents/${Date.now()}_${safeName}`;
    const storageRef = ref(storage, path);
    const snapshot = await uploadBytes(storageRef, file);
    return getDownloadURL(snapshot.ref);
  };

  const handleDocumentUpload = (e) => {
    const files = Array.from(e.target.files);
    const newDocs = files.map(file => ({
      name: file.name,
      displayName: file.name, // Default to filename, but user can edit
      size: file.size,
      type: file.type,
      uploadedAt: new Date().toISOString(),
      // Uploaded to Storage on save; omitted from persisted lesson data
      file
    }));
    setDocuments(prev => [...prev, ...newDocs]);
    e.target.value = '';
  };

  const handleRemoveDocument = (index) => {
    setDocuments(prev => prev.filter((_, i) => i !== index));
  };

  const handleDocumentNameChange = (index, newName) => {
    setDocuments(prev => prev.map((doc, i) => 
      i === index ? { ...doc, displayName: newName } : doc
    ));
  };

  const handleEdit = () => {
    setBackup({ title, selectedModule, description, videoUrl, videoLength, documents });
    setEditMode(true);
  };

  const handleCancel = () => {
    setTitle(backup.title);
    setSelectedModule(backup.selectedModule);
    setDescription(backup.description);
    setVideoUrl(backup.videoUrl);
    setVideoLength(backup.videoLength);
    setDocuments(backup.documents || []);
    setEditMode(false);
  };

  const handleSave = async () => {
    try {
      console.log('Selected module:', selectedModule);
      console.log('Available modules:', modules);

      if (!selectedModule) {
        setError('Please select a module');
        return;
      }

      // Verify the selected module exists
      const moduleExists = modules.some(m => m.id === selectedModule);
      if (!moduleExists) {
        setError('Selected module does not exist');
        return;
      }

      if (videoUploading) {
        setError('Wait for the video to finish uploading.');
        return;
      }

      const processedDocuments = [];
      try {
        setUploading(true);
        for (const d of documents) {
          if (d.file instanceof File) {
            const url = await uploadDocumentToStorage(d.file);
            processedDocuments.push({
              name: d.name,
              displayName: d.displayName || d.name,
              size: d.size,
              type: d.type,
              uploadedAt: d.uploadedAt,
              url
            });
          } else if (d.url) {
            processedDocuments.push({
              name: d.name,
              displayName: d.displayName || d.name,
              size: d.size,
              type: d.type,
              uploadedAt: d.uploadedAt,
              url: d.url
            });
          } else {
            setError(`Document "${d.displayName || d.name}" has no download link. Remove it and upload the file again.`);
            return;
          }
        }
      } catch (docErr) {
        console.error('Document upload failed:', docErr);
        setError(docErr.message || 'Failed to upload one or more documents.');
        return;
      } finally {
        setUploading(false);
      }

      const lessonData = {
        title,
        description,
        videoUrl,
        videoLength,
        documents: processedDocuments,
        order
      };

      console.log('Saving lesson with data:', lessonData);

      if (isNewLesson) {
        // Create new lesson
        console.log('Creating new lesson...');
        const newLesson = await addLessonToModule(courseId, selectedModule, lessonData);
        console.log('Created new lesson:', newLesson);
        
        // Navigate to the new lesson
        navigate(`/crm/course-templates/${courseId}/lessons/${newLesson.id}`, { replace: true });
      } else {
        // Update existing lesson
        console.log('Updating existing lesson...');
        await updateLesson(courseId, selectedModule, lessonId, lessonData);
        console.log('Updated lesson successfully');
        setDocuments(processedDocuments);
        setEditMode(false);
        setError(null);
      }
    } catch (err) {
      console.error('Error saving lesson:', err);
      setError('Failed to save lesson. Please try again.');
    }
  };

  const handleDelete = async () => {
    if (!selectedModule || !lessonId) {
      setError('Module or lesson ID is missing');
      return;
    }

    if (window.confirm('Are you sure you want to delete this lesson?')) {
      try {
        await deleteLesson(courseId, selectedModule, lessonId);
        navigate(-1);
      } catch (err) {
        console.error('Error deleting lesson:', err);
        setError('Failed to delete lesson. Please try again.');
      }
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-center">
          <BBALoader size={48} className="mx-auto" />
          <p className={`mt-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading lesson data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-center text-red-600">
          <p>{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 relative z-0">
      <PortalBackground />
      <div className="relative max-w-5xl mx-auto">
        {/* Top Row: Back, Title, Edit */}
        <div className="flex items-center justify-between mb-6 gap-2">
          <BackButton onClick={() => navigate(-1)} />
          <h1 className={`text-3xl font-bold tracking-tight text-center flex-1 text-gray-900 dark:text-white`}>{isNewLesson ? 'New Lesson' : title}</h1>
          <div className="flex gap-2 ml-4 flex-shrink-0">
            {!editMode ? (
              <EditButton onClick={handleEdit} />
            ) : (
              <>
                {!isNewLesson && (
                  <button onClick={handleDelete} className="flex items-center gap-1 px-4 py-2 rounded-md bg-red-100 text-red-700 font-semibold hover:bg-red-200">
                    <Trash2 className="h-4 w-4" /> Delete
                  </button>
                )}
                <button onClick={handleCancel} className="flex items-center gap-1 px-4 py-2 rounded-md bg-gray-200 text-gray-700 font-semibold hover:bg-gray-300">
                  <X className="h-4 w-4" /> Cancel
                </button>
                <SaveButton onClick={handleSave} disabled={uploading || videoUploading} />
              </>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 gap-6">
          {/* Main Form */}
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-sm p-6`}>
            <h2 className={`text-lg font-semibold mb-4 text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Lesson Details</h2>
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Title</label>
              <input type="text" className={`w-full border rounded-md px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`} value={title} onChange={e => setTitle(e.target.value)} readOnly={!editMode} />
            </div>
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Select module</label>
              <select 
                className={`w-full border rounded-md px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                value={selectedModule} 
                onChange={e => {
                  console.log('Module selection changed:', e.target.value); // Debug log
                  setSelectedModule(e.target.value);
                }} 
                disabled={!editMode}
              >
                <option value="">Select a module</option>
                {modules.map(m => (
                  <option key={m.id} value={m.id}>{m.title}</option>
                ))}
              </select>
            </div>
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Video{videoLength && <span className={`ml-2 font-normal ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{videoLength}</span>}
              </label>
              {editMode ? (
                <CourseVideoUploader
                  courseId={courseId}
                  lessonId={isNewLesson ? null : lessonId}
                  videoUrl={videoUrl}
                  darkMode={isDarkMode}
                  disabled={uploading}
                  onUploadingChange={setVideoUploading}
                  onChange={(v) => {
                    setVideoUrl(v.videoUrl);
                    setVideoLength(v.videoLength);
                  }}
                />
              ) : (
                <>
                  {videoUrl ? (
                    <div className="w-full">
                      <video key={videoUrl} controls src={videoUrl} className="w-full rounded-md" style={{ maxHeight: '600px' }}>
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  ) : (
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      No video uploaded yet.
                    </p>
                  )}
                </>
              )}
            </div>
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Description</label>
              <textarea className={`w-full border rounded-md px-3 py-2 min-h-[120px] ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`} value={description} onChange={e => setDescription(e.target.value)} readOnly={!editMode} />
            </div>
            {/* Documents Section */}
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Documents</label>
              {editMode ? (
                <>
                  <div className="mb-3">
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx"
                      className="hidden"
                      id="document-upload"
                      onChange={handleDocumentUpload}
                    />
                    <label 
                      htmlFor="document-upload"
                      className={`inline-flex items-center gap-2 px-4 py-2 border rounded-md cursor-pointer hover:bg-gray-50 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600' : 'bg-white border-gray-300'}`}
                    >
                      <Upload className="h-4 w-4" />
                      Upload Documents
                    </label>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Supported formats: PDF, DOC, DOCX, TXT, XLS, XLSX, PPT, PPTX
                    </p>
                  </div>
                  {documents.length > 0 && (
                    <div className="space-y-2">
                      {documents.map((doc, idx) => (
                        <div key={idx} className={`flex items-center justify-between p-3 rounded-md ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                          <div className="flex items-center gap-3 flex-1">
                            <FileText className="h-4 w-4 text-gray-500 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <input
                                type="text"
                                className={`w-full border rounded-md px-2 py-1 text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                                value={doc.displayName}
                                onChange={(e) => handleDocumentNameChange(idx, e.target.value)}
                                placeholder="Enter display name (optional)"
                              />
                              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                Original: {doc.name} ({(doc.size / 1024 / 1024).toFixed(2)} MB)
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveDocument(idx)}
                            className="text-red-500 hover:text-red-700 flex-shrink-0 ml-2"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="space-y-2">
                  {documents.length === 0 ? (
                    <p className={`italic ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No documents uploaded</p>
                  ) : (
                    documents.map((doc, idx) => (
                      <div key={idx} className={`p-3 rounded-md ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <FileText className="h-4 w-4 text-gray-500" />
                          <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {doc.displayName || doc.name}
                          </span>
                        </div>
                        {doc.displayName && doc.displayName !== doc.name && (
                          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} ml-6`}>
                            Original: {doc.name}
                          </p>
                        )}
                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} ml-6`}>
                          ({(doc.size / 1024 / 1024).toFixed(2)} MB)
                        </p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LessonOverview;